import { useCallback, useEffect, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tooltip } from '@material-ui/core';
import { DownloadModal } from '@components/Modals';
import { setIsOpenDownloadModal } from '@actions/lettersPageActions';
import { setNotification } from '@actions/appActions';
import {
  generateSubmissionEml,
  generatePdf,
  getMgLocalStorage,
  setMgLocalStorage,
  generateSubmissionICS,
} from '@utils';
import { DownloadIcon } from '@images/icons';
import { MgState } from '@types';
import { DownloadFormats } from '@constants/downloadFormats';
import { MgLocalStorageKeys } from '@enums';
import {
  currentSubmissionInitialTitleSelector,
  currentSubmissionSelector,
  currentSubmissionTitleSelector,
  submissionDownloadableSelector,
  submissionFormDownloadExtensionsSelector,
  submissionsIsEmptySelector,
} from '@selectors/submission';
import { loading } from '@actions/commonActions';

export interface DownloadControllerProps {
  description: string;
}

const DownloadController: FC<DownloadControllerProps> = ({ description }) => {
  const dispatch = useDispatch();
  const [letterName, setLetterName] = useState('');
  const [formatValue, setFormatValue] = useState<DownloadFormats>(null);

  const currentSubmission = useSelector(currentSubmissionSelector);
  const currentSubmissionTitle = useSelector(currentSubmissionTitleSelector);
  const currentSubmissionInitialTitle = useSelector(currentSubmissionInitialTitleSelector);
  const submissionsIsEmpty = useSelector<MgState, boolean>(submissionsIsEmptySelector);

  const submissionFormDownloadExtensions = useSelector<MgState, DownloadFormats[]>(
    submissionFormDownloadExtensionsSelector
  );
  const isOpenDownloadModal = useSelector<MgState, boolean>(
    ({ lettersPage }) => lettersPage.isOpenDownloadModal
  );
  const isSubmissionDownloadLoading = useSelector<MgState, boolean>(
    ({ submission }) => submission.isSubmissionDownloadLoading
  );
  const isSubmissionDownloadable = useSelector(submissionDownloadableSelector);

  const handleDownload = useCallback(async () => {
    dispatch(loading(true, 'bar'));
    dispatch(loading(true, 'download_submission'));
    try {
      switch (formatValue) {
        case DownloadFormats.PDF:
          await generatePdf(false, letterName, currentSubmission);
          break;
        case DownloadFormats.EML:
          await generateSubmissionEml(false, letterName, currentSubmission);
          break;
        case DownloadFormats.ICS:
          await generateSubmissionICS(letterName, currentSubmission);
          break;
        default:
          await generateSubmissionEml(true, letterName, currentSubmission);
      }
      dispatch(setNotification({ textKey: 'template_has_been_downloaded', variant: 'success' }));
    } catch (err) {
      dispatch(setNotification({ textKey: 'error', variant: 'error' }));
    } finally {
      setLetterName(currentSubmissionTitle);
      dispatch(setIsOpenDownloadModal(false));
      dispatch(loading(false, 'bar'));
      dispatch(loading(false, 'download_submission'));
    }
  }, [formatValue, generateSubmissionEml, generatePdf, currentSubmission, letterName]);

  useEffect(() => {
    const format = getMgLocalStorage<DownloadFormats>(MgLocalStorageKeys.LAST_USED_OPTION);
    if (submissionFormDownloadExtensions?.includes(format)) {
      setFormatValue(format);
    } else if (submissionFormDownloadExtensions?.length === 1) {
      setFormatValue(submissionFormDownloadExtensions[0]);
    }
  }, [isOpenDownloadModal]);

  useEffect((): void => {
    if (currentSubmissionInitialTitle) {
      setLetterName(currentSubmissionInitialTitle);
    }
  }, [currentSubmissionInitialTitle]);

  const handleCancelDownload = useCallback(() => {
    dispatch(setIsOpenDownloadModal(false));
  }, []);

  const handleChangeFormat = useCallback(
    (newValue: DownloadFormats) => {
      setFormatValue(newValue);
      setMgLocalStorage(MgLocalStorageKeys.LAST_USED_OPTION, newValue);
    },
    [setMgLocalStorage]
  );

  const handleOnLetterNameChange = (event) => {
    setLetterName(event.currentTarget.value);
  };

  return (
    <>
      <Tooltip title={description} arrow>
        <span>
          <Button
            size="large"
            color="primary"
            onClick={() => dispatch(setIsOpenDownloadModal(true))}
            disabled={submissionsIsEmpty || !isSubmissionDownloadable}
            disableRipple
          >
            <DownloadIcon />
          </Button>
        </span>
      </Tooltip>

      <DownloadModal
        open={isOpenDownloadModal}
        value={letterName}
        format={formatValue}
        contentName="download_modal_input_name"
        handleCancel={handleCancelDownload}
        handleConfirmation={handleDownload}
        handleChangeFormat={handleChangeFormat}
        onChange={handleOnLetterNameChange}
        isDisabledConfirmationButton={isSubmissionDownloadLoading}
      />
    </>
  );
};

export { DownloadController };
